import dayjs, { Dayjs } from "dayjs";
import { EMPTY_STR } from "@/constants/string";

export type TTalentFilterFormExtended = {
  talentNumber: string;
  fullName: string;
  nickName: string;
  nationalityId: string[];
  gender: string[];
  recruitmentStatus: string[];
  latestProgress: string[];
  residenceStatusId: string[];
  languageTestLevelId: string[];
  companySpecificIndustryId: string[];
  personInChargeId: string[];
  registrationDateFrom: Dayjs | null;
  registrationDateTo: Dayjs | null;
  desiredWorkLocation: string;
  location: string[];
  heldQualifications: string;
  educationLevelId: string[];
  isRecommendedTalent: string;
  partnerId: string[];
};

export type TTalentDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const FILTER_LABEL_MAX_LENGTH = 30;
export const FILTER_LABEL_FULL_WIDTH_MAX_LENGTH = 60;

export const defaultTalentFilterValues: TTalentFilterFormExtended = {
  talentNumber: "",
  fullName: "",
  nickName: "",
  nationalityId: [],
  gender: [],
  recruitmentStatus: [],
  latestProgress: [],
  residenceStatusId: [],
  languageTestLevelId: [],
  companySpecificIndustryId: [],
  personInChargeId: [],
  registrationDateFrom: null,
  registrationDateTo: null,
  desiredWorkLocation: "",
  location: [],
  heldQualifications: "",
  educationLevelId: [],
  isRecommendedTalent: "",
  partnerId: [],
};

export const defaultTalentDisplayColumns: TTalentDisplayColumnSetting[] = [
  { id: "recruitmentStatus", label: "ステータス", visible: true, order: 1 },
  { id: "talentNumber", label: "人材管理番号", visible: true, order: 2 },
  { id: "fullName", label: "氏名", visible: true, order: 3 },
  { id: "nickName", label: "呼び名", visible: true, order: 4 },
  { id: "nationalityName", label: "国籍", visible: true, order: 5 },
  { id: "gender", label: "性別", visible: true, order: 6 },
  { id: "caseMatchStatus", label: "最新進捗", visible: true, order: 7 },
  { id: "residenceStatusId", label: "在留資格", visible: true, order: 8 },
  { id: "languageCertificates", label: "日本語能力", visible: true, order: 9 },
  { id: "companySpecificIndustry", label: "分野", visible: true, order: 10 },
  { id: "location", label: "居住地", visible: true, order: 11 },
  { id: "personInChargeId", label: "担当者", visible: true, order: 12 },
  { id: "createdAt", label: "登録日", visible: true, order: 13 },
  { id: "desiredWorkLocation", label: "希望勤務地", visible: true, order: 14 },
  { id: "heldQualifications", label: "資格", visible: true, order: 15 },
  { id: "educationLevel", label: "学歴", visible: true, order: 16 },
  { id: "partnerName", label: "パートナー名", visible: true, order: 17 },
];

/**
 * Merge persisted columns (localStorage / saved condition) with the current defaults.
 * Keeps saved order + visibility for known columns and appends any newly-added default
 * column missing from the saved set (so new columns appear for existing users).
 */
export const mergeWithDefaultTalentColumns = (saved?: TTalentDisplayColumnSetting[] | null): TTalentDisplayColumnSetting[] => {
  if (!saved?.length) return [...defaultTalentDisplayColumns];
  const savedIds = new Set(saved.map((col) => col.id));
  const missing = defaultTalentDisplayColumns.filter((col) => !savedIds.has(col.id));
  // Drop persisted columns no longer defined in defaults, then append the new ones
  const known = saved.filter((col) => defaultTalentDisplayColumns.some((d) => d.id === col.id));
  return [...known, ...missing].map((col, index) => ({ ...col, order: index + 1 }));
};

// Normalize date fields from strings (JSON) to Dayjs objects for DatePicker compatibility
export const normalizeTalentDateFields = (filters: Partial<TTalentFilterFormExtended>): Partial<TTalentFilterFormExtended> => {
  const result = { ...filters };
  if (result.registrationDateFrom && typeof result.registrationDateFrom === "string") {
    const day = dayjs(result.registrationDateFrom);
    result.registrationDateFrom = day.isValid() ? day : null;
  }
  if (result.registrationDateTo && typeof result.registrationDateTo === "string") {
    const day = dayjs(result.registrationDateTo);
    result.registrationDateTo = day.isValid() ? day : null;
  }
  return result;
};

// Serialize title + filters for dirty-checking comparison
export const serializeTalentForComparison = (title: string, filters: TTalentFilterFormExtended): string => {
  const normalized = { ...filters };
  if (normalized.registrationDateFrom) {
    normalized.registrationDateFrom = dayjs(normalized.registrationDateFrom).format(
      "YYYY-MM-DD",
    ) as unknown as typeof normalized.registrationDateFrom;
  }
  if (normalized.registrationDateTo) {
    normalized.registrationDateTo = dayjs(normalized.registrationDateTo).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateTo;
  }
  return JSON.stringify({ title, filters: normalized });
};

export const TALENT_FILTER_FIELD_LABELS: { key: keyof TTalentFilterFormExtended; label: string }[] = [
  { key: "recruitmentStatus", label: "ステータス" },
  { key: "talentNumber", label: "人材管理番号" },
  { key: "fullName", label: "氏名" },
  { key: "nickName", label: "呼び名" },
  { key: "nationalityId", label: "国籍" },
  { key: "gender", label: "性別" },
  { key: "latestProgress", label: "最新進捗" },
  { key: "residenceStatusId", label: "在留資格" },
  { key: "languageTestLevelId", label: "日本語能力" },
  { key: "companySpecificIndustryId", label: "分野" },
  { key: "location", label: "居住地" },
  { key: "personInChargeId", label: "担当者" },
  { key: "desiredWorkLocation", label: "希望勤務地" },
  { key: "heldQualifications", label: "資格" },
  { key: "educationLevelId", label: "学歴" },
  { key: "isRecommendedTalent", label: "おすすめ人材" },
  { key: "partnerId", label: "パートナー名" },
];

export const TALENT_DATE_RANGE_FIELD = {
  fromKey: "registrationDateFrom" as const,
  toKey: "registrationDateTo" as const,
  label: "登録日",
};

/** Check if a filter value is empty (empty string, null, undefined, or empty array) */
export const isEmptyTalentFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyTalentFilterFields = (filters: TTalentFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyTalentFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

/** Multi-select filter field keys */
export const TALENT_MULTI_SELECT_FILTER_KEYS: (keyof TTalentFilterFormExtended)[] = [
  "nationalityId",
  "gender",
  "recruitmentStatus",
  "latestProgress",
  "residenceStatusId",
  "languageTestLevelId",
  "companySpecificIndustryId",
  "personInChargeId",
  "location",
  "educationLevelId",
  "partnerId",
];

/** Normalize legacy scalar filter values to arrays for backward compatibility with saved conditions */
export const normalizeTalentMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TTalentFilterFormExtended> => {
  const result = { ...filters };
  for (const key of TALENT_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    // Convert legacy scalar value to array (0 is valid, e.g. ECaseMatchStatus.BEFORE_PROPOSAL)
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TTalentFilterFormExtended>;
};

export const formatTalentDateValue = (value: Dayjs | string | null): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY/MM/DD") : EMPTY_STR.DATE;
};

export const TALENT_LOCKED_COLUMN_IDS = ["talentNumber", "fullName"];
